import { useDrag } from '@use-gesture/react';
import { useEffect, useRef } from 'react';

/**
 * Which edge of the panel the resize handle sits on.
 * Controls how drag direction maps to width change:
 * - `'left'`  — handle on left edge (right-side panel): drag left = wider
 * - `'right'` — handle on right edge (left-side panel): drag right = wider
 */
type ResizeDirection = 'left' | 'right';

interface UseResizePxProps {
  /** Current width in pixels (controlled) */
  width: number;
  /** Callback to update width */
  onWidthChange: (width: number) => void;
  /** Minimum width in pixels */
  minWidth: number;
  /** Maximum width in pixels */
  maxWidth: number;
  /** Which edge the resize handle is on. Defaults to `'left'` (right-side panel). */
  direction?: ResizeDirection;
}

interface UseResizePxReturn {
  /** Whether the user is currently dragging the resize handle */
  isResizing: boolean;
  /** Ref to attach to the resize handle element */
  resizeRef: React.RefObject<HTMLDivElement>;
}

/**
 * Pixel-based resize hook for controlled panel widths.
 * Mirrors the API pattern of `src/hooks/useResize.ts` but works with pixel
 * values and controlled state (width is owned by the parent).
 *
 * Use `direction` to control which edge the handle sits on:
 * - `'left'`  (default) — right-side panel, dragging left increases width
 * - `'right'` — left-side panel, dragging right increases width
 */
export function useResizePx({
  width,
  onWidthChange,
  minWidth,
  maxWidth,
  direction = 'left',
}: UseResizePxProps): UseResizePxReturn {
  const isResizing = useRef(false);
  const resizeRef = useRef<HTMLDivElement>(null);
  const startWidth = useRef(width);

  const sign = direction === 'left' ? -1 : 1;

  useDrag(
    ({ first, dragging, movement: [mx] }) => {
      if (first) {
        startWidth.current = width;
      }

      if (dragging) {
        isResizing.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const newWidth = Math.round(
          Math.min(maxWidth, Math.max(minWidth, startWidth.current + sign * mx)),
        );
        onWidthChange(newWidth);
      } else {
        isResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    },
    { target: resizeRef },
  );

  useEffect(() => {
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  return {
    isResizing: isResizing.current,
    resizeRef,
  };
}
